import { useMemo, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { MdCheckCircle, MdPlayArrow } from 'react-icons/md';
import { Button } from '../../../Components/Button';
import { BatchItem, BatchJob } from '../CommerceScraperUtils';
import { BatchProgress } from '../ScraperResults';
import { getOptimizableFields, optimizeListing } from './ai-optimize-utils';
import { OptimizationResult } from './ai-optimize-constants';
import { ProductProposal, ProposalStore } from './proposals-utils';
import { AuditedProduct } from './scoring-engine-constants';
import { formatAiError } from './title-compliance-utils';

const CONCURRENCY = 3;

interface BatchOptimizeModalProps {
  products: AuditedProduct[];
  proposals: ProposalStore;
  onSaveProposal: (productId: string, proposal: ProductProposal | null) => void;
  onClose: () => void;
}

export const BatchOptimizeModal = ({
  products,
  proposals,
  onSaveProposal,
  onClose
}: BatchOptimizeModalProps) => {
  // Candidates are unoptimized (<90%) listings that don't already have a proposal —
  // skipping existing proposals avoids overwriting curated work. Frozen to the
  // modal's opening snapshot (empty deps) so the list doesn't shrink mid-run as
  // onSaveProposal updates the `proposals` prop for each completed item — otherwise
  // `allDone` (completed vs. candidates.length) would flip true around the
  // halfway point of the batch.
  const candidates = useMemo(
    () =>
      products.filter(
        p => p.finalScore < 90 && !proposals[p.id] && getOptimizableFields(p).length > 0
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const [started, setStarted] = useState(false);
  const [job, setJob] = useState<BatchJob>({
    id: 'local',
    type: 'batch-optimize',
    platform: 'amazon',
    status: 'running',
    total: candidates.length,
    completed: 0,
    items: candidates.map(p => ({ label: `${p.asin} · ${p.title}`, status: 'pending' as const }))
  });
  const cancelledRef = useRef(false);

  const buildAutoAcceptedProposal = (
    product: AuditedProduct,
    res: OptimizationResult
  ): ProductProposal => ({
    productId: product.id,
    generatedAt: Date.now(),
    title: res.title ? { ...res.title, accepted: true } : undefined,
    bullets: res.bullets ? { ...res.bullets, accepted: true } : undefined,
    description: res.description ? { ...res.description, accepted: true } : undefined,
    ...(res.alexaScore !== null && res.alexaScore !== undefined
      ? { alexaScore: res.alexaScore }
      : {})
  });

  const updateItem = (index: number, patch: Partial<BatchItem>) => {
    setJob(prev => {
      const items = [...prev.items];
      items[index] = { ...items[index], ...patch };
      return { ...prev, items };
    });
  };

  const runOne = async (index: number) => {
    if (cancelledRef.current) return;
    updateItem(index, { status: 'running' });
    try {
      const res = await optimizeListing(candidates[index]);
      if (cancelledRef.current) return;
      onSaveProposal(candidates[index].id, buildAutoAcceptedProposal(candidates[index], res));
      updateItem(index, { status: 'done' });
    } catch (e) {
      if (cancelledRef.current) return;
      updateItem(index, { status: 'failed', error: formatAiError(e) });
    }
    setJob(prev => ({ ...prev, completed: prev.completed + 1 }));
  };

  const startBatch = async () => {
    setStarted(true);
    let cursor = 0;
    const worker = async () => {
      while (cursor < candidates.length) {
        const index = cursor++;
        await runOne(index);
      }
    };
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, candidates.length) }, worker));
    setJob(prev => ({ ...prev, status: 'complete' }));
  };

  const handleClose = () => {
    cancelledRef.current = true;
    onClose();
  };

  const completedCount = job.items.filter(i => i.status === 'done').length;
  const errorCount = job.items.filter(i => i.status === 'failed').length;
  const allDone = started && completedCount + errorCount >= candidates.length;

  const getFootnoteText = (): string => {
    if (allDone) {
      return errorCount > 0
        ? `Done — ${completedCount} succeeded, ${errorCount} failed. Reopen this batch later to retry the failed listings.`
        : 'Done. Review pending proposals in the catalog or export the CSV for the content team.';
    }
    if (started) return 'Running…';
    return 'Nothing happens until you click Start.';
  };

  const failedItems = job.items.filter(i => i.status === 'failed');

  return (
    <Modal show centered size='lg' onHide={handleClose} className='scorecard-batch-modal'>
      <Modal.Header closeButton>
        <div>
          <span className='scorecard-modal-eyebrow'>Batch AI Optimization</span>
          <Modal.Title>
            {candidates.length} listing{candidates.length === 1 ? '' : 's'} eligible for
            optimization
          </Modal.Title>
          <p className='scorecard-modal-subtitle'>
            All sub-90% listings without an existing proposal. Suggestions are auto-accepted;
            uncheck per-listing afterward to refine.
          </p>
        </div>
      </Modal.Header>
      <Modal.Body>
        {candidates.length === 0 ? (
          <div className='scorecard-modal-success-banner'>
            <MdCheckCircle />
            <p>No listings need batch optimization right now.</p>
          </div>
        ) : (
          <>
            <BatchProgress job={job} />
            {failedItems.length > 0 && (
              <div className='scorecard-batch-rows'>
                {failedItems.map(item => (
                  <div key={item.label} className='scorecard-batch-row'>
                    <div className='scorecard-batch-row-info'>
                      <span className='scorecard-batch-row-title'>{item.label}</span>
                      <div className='scorecard-batch-row-error'>{item.error}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <p className='scorecard-modal-footnote'>{getFootnoteText()}</p>
        {!started && candidates.length > 0 && (
          <Button leadingElement={<MdPlayArrow />} onClick={startBatch}>
            Start Batch Optimization
          </Button>
        )}
        {(allDone || candidates.length === 0) && <Button onClick={handleClose}>Close</Button>}
      </Modal.Footer>
    </Modal>
  );
};
